import { ClusteredDataPoint } from 'libs/end-to-end/src/copilot';
import { CopilotOutput } from 'libs/end-to-end/src/copilot';
import { SlateCopilotDataPoint } from 'libs/end-to-end/src/copilot';
import { SlatePoolSubject } from 'libs/end-to-end/src/copilot';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { GetLevelByColumnValueFn } from 'libs/end-to-end/src/dimension';
import { LevelDefinition } from 'libs/end-to-end/src/types/databases/dimensions/level-definition';

export const b = 2;

export const createSublistsWithMaxLengths = <T>(
  arr: T[],
  maxLength: number,
): T[][] => {
  if (maxLength === 0) {
    return [];
  }
  const sublists: T[][] = [];
  let currentList: T[] = [];

  for (let i = 0; i < arr.length; i++) {
    currentList.push(arr[i]);

    if (currentList.length === maxLength || i === arr.length - 1) {
      sublists.push(currentList);
      currentList = [];
    }
  }

  return sublists;
};

export const createClusters = (
  pointData: SlateCopilotDataPoint[],
  maxClusterSize: number,
): ClusteredDataPoint[] => {
  const sublists = createSublistsWithMaxLengths(pointData, maxClusterSize);

  return sublists.map((sublist) => {
    const subjects = sublist.flatMap((point) => point.subjects);
    const numberOfEmployees = sublist.reduce(
      (acc, point) => acc + point.numberOfEmployees,
      0,
    );

    const minimumReadiness = Math.min(
      ...sublist.map((point) => point.readinessRating),
    );

    const maximumReadiness = Math.max(
      ...sublist.map((point) => point.readinessRating),
    );

    const averageReadinessWhole = Math.round(
      sublist.reduce((acc, point) => acc + point.readinessRating, 0) /
        sublist.length,
    );

    return {
      minimumReadiness,
      maximumReadiness,
      averageReadinessWhole,
      numberOfEmployees,
      numberOfSubjects: subjects.length,
    };
  });
};

export type BasicCopilotSortType = 'highest-to-lowest' | 'lowest-to-highest';

const sortSubjectsById = (
  a: SlatePoolSubject,
  b: SlatePoolSubject,
  sortType: BasicCopilotSortType,
) => {
  if (sortType === 'highest-to-lowest') {
    return a.id.localeCompare(b.id);
  } else if (sortType === 'lowest-to-highest') {
    return b.id.localeCompare(a.id);
  } else {
    throw guardExhaustive(sortType);
  }
};

const sortSubjectsByReadinessRating = (
  a: SlatePoolSubject,
  b: SlatePoolSubject,
  sortType: BasicCopilotSortType,
) => {
  if (sortType === 'highest-to-lowest') {
    return b.readinessRating - a.readinessRating;
  } else if (sortType === 'lowest-to-highest') {
    return a.readinessRating - b.readinessRating;
  } else {
    throw guardExhaustive(sortType);
  }
};

export const sortSubjects = (
  subjects: SlatePoolSubject[],
  sortType: BasicCopilotSortType,
) => {
  return subjects
    .sort((a, b) => sortSubjectsById(a, b, sortType))
    .sort((a, b) => sortSubjectsByReadinessRating(a, b, sortType));
};

export const createPrefilledSubjectIds = (output: CopilotOutput): string[] => {
  return output
    .sort((a, b) => {
      return b.readinessRating - a.readinessRating;
    })
    .flatMap((group) => group.subjects)
    .map((subject) => subject.id)
    .slice(0, 20);
};

export const createSlatePoolSubjectsFromOutput = (
  output: CopilotOutput,
  getLevelByColumnValue: GetLevelByColumnValueFn,
  selectedSlateIds: string[],
): SlatePoolSubject[] => {
  return output.flatMap((group) => {
    return group.subjects.map((subject) => {
      const levelDefinitions: LevelDefinition[] = [];
      const raceLevelDefinition = subject.race
        ? getLevelByColumnValue('Race', subject.race)
        : undefined;
      if (raceLevelDefinition) {
        levelDefinitions.push(raceLevelDefinition);
      }
      const genderLevelDefinition = subject.gender
        ? getLevelByColumnValue('Gender', subject.gender)
        : undefined;
      if (genderLevelDefinition) {
        levelDefinitions.push(genderLevelDefinition);
      }
      return {
        ...subject,
        isInSlate: selectedSlateIds.includes(subject.id),
        readinessRating: group.readinessRating,
        levelDefinitions,
        sortableTotalCompensation:
          subject.compensationFigures?.suggestedTotalCompensation ?? 0,
      };
    });
  });
};

export const createSlateCopilotDataPoints = (
  output: CopilotOutput,
  getLevelByColumnValue: GetLevelByColumnValueFn,
  selectedSlateIds: string[],
): SlateCopilotDataPoint[] => {
  return output.map((group) => {
    const subjects = group.subjects.map((subject) => {
      const levelDefinitions: LevelDefinition[] = [];
      const raceLevelDefinition = subject.race
        ? getLevelByColumnValue('Race', subject.race)
        : undefined;
      if (raceLevelDefinition) {
        levelDefinitions.push(raceLevelDefinition);
      }
      const genderLevelDefinition = subject.gender
        ? getLevelByColumnValue('Gender', subject.gender)
        : undefined;
      if (genderLevelDefinition) {
        levelDefinitions.push(genderLevelDefinition);
      }
      return {
        ...subject,
        isInSlate: selectedSlateIds?.includes(subject.id) ?? false,
        readinessRating: group.readinessRating,
        levelDefinitions,
        sortableTotalCompensation:
          subject.compensationFigures?.suggestedTotalCompensation ?? 0,
      };
    });

    return {
      ...group,
      numberOfSubjects: subjects.length,
      subjects,
    };
  });
};

export const extractFirstCopilotDataPointIdForReadiness = ({
  dataPoints,
  maximumReadiness,
  minimumReadiness,
}: {
  dataPoints: SlateCopilotDataPoint[];
  maximumReadiness: number;
  minimumReadiness: number;
  sortType: BasicCopilotSortType;
}): string | undefined => {
  const subjects = dataPoints
    .filter(
      (point) =>
        point.readinessRating >= minimumReadiness &&
        point.readinessRating <= maximumReadiness,
    )
    .flatMap((point) => point.subjects);

  return sortSubjects(subjects, 'highest-to-lowest').at(0)?.id;
};

export const allSubjectsFromDataPoints = (
  dataPoints: SlateCopilotDataPoint[],
): SlatePoolSubject[] => {
  return dataPoints.flatMap((point) => point.subjects);
};

export const getSelectedClusteredDataPoint = ({
  clusteredDataPoints,
  selectedSubject,
}: {
  clusteredDataPoints: ClusteredDataPoint[];
  selectedSubject: SlatePoolSubject | null;
}): ClusteredDataPoint | null => {
  if (!selectedSubject) {
    return null;
  }
  return (
    clusteredDataPoints.find((cluster) => {
      return (
        selectedSubject.readinessRating >= cluster.minimumReadiness &&
        selectedSubject.readinessRating <= cluster.maximumReadiness
      );
    }) ?? null
  );
};

export const getSubjectsForClusteredDataPoint = ({
  clusteredDataPoint,
  dataPoints,
  sortType,
}: {
  clusteredDataPoint: ClusteredDataPoint | null;
  dataPoints: SlateCopilotDataPoint[];
  sortType: BasicCopilotSortType;
}): SlatePoolSubject[] => {
  if (!clusteredDataPoint) {
    return [];
  }
  return sortSubjects(
    allSubjectsFromDataPoints(dataPoints).filter(
      (subject) =>
        subject.readinessRating >= clusteredDataPoint.minimumReadiness &&
        subject.readinessRating <= clusteredDataPoint.maximumReadiness,
    ),
    sortType,
  );
};
